import { type EntitlementsDto } from '@osteopro/shared';
import { Alert, AlertDescription, Button } from '@osteopro/ui';
import { Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'wouter';
import { useEntitlements } from '../../hooks/api/use-plans.js';

/**
 * Bandeau d'essai, visible pendant tout l'essai. Les sept derniers jours insistent (jours
 * restants seuls) ; avant, un rappel plus calme nomme l'offre Professionnel, les jours restants et
 * l'absence de carte enregistrée.
 *
 * Un cabinet **bloqué** n'a plus de bandeau : l'écran plein page de blocage le remplace, et les
 * rares pages encore servies (abonnement, retour de caisse) disent elles-mêmes pourquoi.
 *
 * La condition se lit dans les droits, que la barre latérale charge déjà : aucune requête de plus.
 */

/** Jours entiers restants avant l'échéance, jamais négatifs. `null` si l'échéance est inconnue. */
export function daysLeft(currentPeriodEnd: string | null, now: Date): number | null {
  if (currentPeriodEnd === null) return null;
  const end = new Date(currentPeriodEnd).getTime();
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - now.getTime()) / 86_400_000));
}

/** Seuil à partir duquel le ton du bandeau d'essai devient pressant. */
export const TRIAL_URGENT_DAYS = 7;

export function trialBannerUrgent(left: number | null): boolean {
  return left !== null && left <= TRIAL_URGENT_DAYS;
}

/** Un essai échu reste annoncé ce délai-là : la tâche de nuit qui le clôt n'est peut-être pas passée. */
const EXPIRED_TRIAL_GRACE_MS = 86_400_000;

/** Visible pendant tout l'essai, et jamais sur un cabinet bloqué. */
export function trialBannerVisible(entitlements: EntitlementsDto | undefined, now: Date): boolean {
  if (entitlements === undefined || entitlements.readOnly) return false;
  if (entitlements.status !== 'trialing' || entitlements.currentPeriodEnd === null) return false;
  const end = new Date(entitlements.currentPeriodEnd).getTime();
  if (Number.isNaN(end)) return false;
  return end > now.getTime() - EXPIRED_TRIAL_GRACE_MS;
}

function trialMessageKey(left: number | null): 'plans:trial.ending' | 'plans:trial.active' {
  return trialBannerUrgent(left) ? 'plans:trial.ending' : 'plans:trial.active';
}

export function TrialBanner() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const entitlements = useEntitlements();
  const data = entitlements.data;

  if (data === undefined || !trialBannerVisible(data, new Date())) return null;

  const left = daysLeft(data.currentPeriodEnd, new Date());

  return (
    <Alert
      variant="default"
      className="rounded-none border-x-0 border-t-0"
      data-testid="banner-trial"
      data-tone={trialBannerUrgent(left) ? 'urgent' : 'calm'}
    >
      <Clock className="h-4 w-4" />
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="min-w-0 flex-1">{t(trialMessageKey(left), { count: left ?? 0 })}</span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            navigate('/settings/subscription');
          }}
          data-testid="button-trial-subscribe"
        >
          {t('plans:trial.subscribe')}
        </Button>
      </AlertDescription>
    </Alert>
  );
}
